import * as React from 'react';
import { hasPermission, useSession } from '@cguard/auth';
import { Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useCameraList } from '@cguard/api-client/hooks/cameras';
import { deleteEvidenceExport, downloadEvidence, evidenceKey, recordingCoverage, subscribeExportChanges, useEvidenceExports, useStartEvidenceExport } from '@cguard/api-client/hooks/evidence-exports';
import { DateTimePicker } from '@cguard/design-system';
import { useTranslation } from 'react-i18next';
import { useSiteNames } from '~/lib/site-names';
import { withoutSeededDemoCameras } from '~/lib/production-roster';
import { siteInputToDate, toSiteInputValue } from '~/lib/site-time';
import { readActionError } from '~/lib/action-error';

export function ExportsPage({ cameraId: initialCamera = '', incidentId = '', from: initialFrom, to: initialTo }: {
  cameraId?: string; incidentId?: string; from?: string; to?: string;
}): React.JSX.Element {
  const { t } = useTranslation('video');
  const sites = useSiteNames();
  const session = useSession();
  const canManage = hasPermission(session, 'recordings.export.manage');
  const [mine, setMine] = React.useState(true);
  const cameraQuery = useCameraList();
  const cameras = withoutSeededDemoCameras(cameraQuery.data ?? []);
  const [site, setSite] = React.useState('');
  const [camera, setCamera] = React.useState(initialCamera);
  const [search, setSearch] = React.useState('');
  const [offset, setOffset] = React.useState(0);
  const [from, setFrom] = React.useState(() => toSiteInputValue(initialFrom ? new Date(initialFrom) : new Date(Date.now() - 600_000)));
  const [to, setTo] = React.useState(() => toSiteInputValue(initialTo ? new Date(initialTo) : new Date()));
  const [error, setError] = React.useState('');
  const [checking, setChecking] = React.useState(false);
  const [notice, setNotice] = React.useState('');
  const request = React.useRef({ key: '', id: '' });
  const jobs = useEvidenceExports(site, search, offset, mine);
  const start = useStartEvidenceExport();
  const cache = useQueryClient();
  const selectedSite = site || cameras.find((item) => item.id === camera)?.siteId || '';
  React.useEffect(() => {
    const controller = new AbortController();
    // A disconnected feed falls back to the history query's bounded polling.
    void subscribeExportChanges(controller.signal, () => { void cache.invalidateQueries({ queryKey: evidenceKey }); }, mine).catch(() => undefined);
    return () => controller.abort();
  }, [cache, mine]);
  async function submit(): Promise<void> {
    setError(''); setNotice(''); setChecking(true);
    try {
      const first = siteInputToDate(from); const last = siteInputToDate(to);
      if (!incidentId && (!camera || !first || !last || last <= first || last.getTime() - first.getTime() > 86400000 || last.getTime() > Date.now())) throw new Error(t('exports.rangeError'));
      if (!incidentId && first && last) {
        const coverage = await recordingCoverage(camera, first.toISOString(), last.toISOString());
        if (!coverage.supported) throw new Error(t('exports.unsupported'));
        if (coverage.gaps.length) throw new Error(t('exports.gaps'));
      }
      const input = incidentId ? { incidentId, ...(camera ? { cameraId: camera } : {}) } : { cameraId: camera, from: first!.toISOString(), to: last!.toISOString() };
      const key = JSON.stringify(input);
      if (request.current.key !== key) request.current = { key, id: crypto.randomUUID() };
      await start.mutateAsync({ ...input, requestId: request.current.id });
      request.current = { key: '', id: '' }; setNotice(t('exports.queued')); setOffset(0);
    } catch (err) { setError(readActionError(err, t('exports.failed'))); }
    finally { setChecking(false); }
  }
  return <section className="evidence-page">
    <div className="viewer-titlebar"><div><p className="eyebrow">{t('nav.exports')}</p><h1>{t('exports.title')}</h1></div></div>
    <form className="evidence-form" onSubmit={(event) => { event.preventDefault(); void submit(); }}>
      <label>{t('exports.site')}<select value={selectedSite} onChange={(event) => { setSite(event.target.value); setCamera(''); setOffset(0); }}>
        <option value="">{t('exports.chooseSite')}</option>
        {[...new Set(cameras.map((item) => item.siteId))].map((id) => <option key={id} value={id}>{sites.get(id) ?? id}</option>)}
      </select></label>
      <label>{t('exports.camera')}<select value={camera} onChange={(event) => setCamera(event.target.value)}>
        <option value="">{incidentId ? t('exports.alarmCamera') : t('exports.chooseCamera')}</option>
        {cameras.filter((item) => item.siteId === selectedSite && item.playbackConfigured).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
      </select></label>
      {incidentId ? <p>{t('exports.incidentWindow')}</p> : <>
        <DateTimePicker label={t('playback.from')} value={from} onChange={setFrom} max={to} />
        <DateTimePicker label={t('playback.to')} value={to} onChange={setTo} min={from} max={toSiteInputValue(new Date())} />
      </>}
      <button className="primary-button" type="submit" disabled={checking || start.isPending || (!incidentId && !camera)}>{checking ? t('exports.checking') : t('exports.create')}</button>
      {error && <p role="alert">{error}</p>}{notice && <p role="status">{notice}</p>}
    </form>
    {canManage && <label><input type="checkbox" checked={!mine} onChange={(event) => { setMine(!event.target.checked); setOffset(0); }} /> {t('exports.allOperators')}</label>}
    <label className="evidence-search">{t('exports.search')}<input type="search" value={search} onChange={(event) => { setSearch(event.target.value); setOffset(0); }} /></label>
    {jobs.isLoading && <p role="status">{t('exports.loading')}</p>}
    {jobs.isError && <p role="alert">{t('exports.failed')} <button className="text-button" onClick={() => void jobs.refetch()}>{t('common:actions.retry')}</button></p>}
    {jobs.data?.length === 0 && <p>{t('exports.empty')}</p>}
    <div className="evidence-list">{jobs.data?.map((job) => <article key={job.id} className="evidence-card">
      <div><h2>{job.cameraName}</h2><p>{sites.get(job.siteId) ?? job.siteId}</p><p>{new Date(job.fromUtc).toLocaleString()} — {new Date(job.toUtc).toLocaleString()}</p></div>
      <p>{t(`exports.phase.${job.phase}`)} · MP4 · {(job.byteSize / 1048576).toFixed(1)} MB</p>
      <p>{t('exports.created')}: {new Date(job.createdAtUtc).toLocaleString()} · {t('exports.expires')}: {new Date(job.expiresAtUtc).toLocaleString()}</p>
      <p className="evidence-id">{job.id} · {t('exports.operator')}: {job.userId}</p>
      {['queued', 'fetching', 'encoding'].includes(job.phase) && <progress aria-label={t('exports.progress')} max={100} value={job.progress} />}
      {job.error && <p role="alert">{job.error}</p>}
      {job.sha256 && <p className="evidence-hash">SHA-256 <code>{job.sha256}</code></p>}
      <div className="titlebar-actions">
        {job.phase === 'ready' && <><EvidenceDownload id={job.id} /><Link className="secondary-button" to="/playback" search={{ exportId: job.id }}>{t('exports.view')}</Link></>}
        {!['cancelled', 'expired'].includes(job.phase) && <button className="secondary-button" onClick={() => {
          void deleteEvidenceExport(job.id).then(() => cache.invalidateQueries({ queryKey: evidenceKey })).catch((err: unknown) => setError(readActionError(err, t('exports.failed'))));
        }}>{t(job.phase === 'ready' || job.phase === 'failed' ? 'exports.delete' : 'exports.cancel')}</button>}
      </div>
    </article>)}</div>
    <div className="titlebar-actions"><button className="secondary-button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 50))}>{t('exports.previous')}</button><button className="secondary-button" disabled={jobs.data?.length !== 50} onClick={() => setOffset(offset + 50)}>{t('exports.next')}</button></div>
  </section>;
}

export function EvidenceDownload({ id, onLoaded }: { id: string; onLoaded?: (url: string) => void }): React.JSX.Element {
  const { t } = useTranslation('video');
  const [progress, setProgress] = React.useState<number | null>(null);
  const [error, setError] = React.useState('');
  const active = React.useRef<AbortController | null>(null);
  const objectUrl = React.useRef<string | null>(null);
  React.useEffect(() => () => { active.current?.abort(); if (objectUrl.current) URL.revokeObjectURL(objectUrl.current); }, [id]);
  async function load(): Promise<void> {
    const controller = new AbortController(); active.current = controller; setProgress(0); setError('');
    try {
      const blob = await downloadEvidence(id, controller.signal, setProgress);
      if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
      const url = URL.createObjectURL(blob); objectUrl.current = url;
      if (onLoaded) onLoaded(url);
      else { const link = document.createElement('a'); link.href = url; link.download = `evidence-${id}.mp4`; link.click(); }
    } catch (err) { if (!controller.signal.aborted) setError(readActionError(err, t('exports.failed'))); }
    finally { active.current = null; setProgress(null); }
  }
  return <div>{progress === null ? <button className="primary-button" onClick={() => void load()}>{t(onLoaded ? 'exports.loadEvidence' : 'exports.download')}</button> : <><progress aria-label={t('exports.progress')} max={100} value={progress} /><button className="secondary-button" onClick={() => active.current?.abort()}>{t('exports.cancel')} ({progress}%)</button></>}{error && <p role="alert">{error}</p>}</div>;
}
export function EvidencePlayer({ id }: { id: string }): React.JSX.Element {
  const { t } = useTranslation('video'); const [url, setUrl] = React.useState<string | null>(null);
  return <section className="evidence-page"><h1>{t('exports.view')}</h1><EvidenceDownload id={id} onLoaded={setUrl} />{url && <video className="evidence-player" controls playsInline src={url} />}<Link to="/exports" className="secondary-button">{t('nav.exports')}</Link></section>;
}
